import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';

/**
 * Hook to fetch and check user permissions.
 * Provides both the permissions data and helper functions for checking permissions.
 * 
 * Usage:
 *   const { permissions, hasPermission, hasAnyPermission, loading } = usePermissions();
 *   
 *   if (hasPermission('tasks.create')) { ... }
 *   if (hasAnyPermission(['tasks.edit_own', 'tasks.edit_any'])) { ... }
 */
export function usePermissions() {
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/permissions/my');
      setPermissions(response.data.permissions || {});
      setRole(response.data.role);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError(err);
      // Set default empty permissions on error
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permissionKey) => {
    return permissions[permissionKey] === true;
  }, [permissions]);

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback((permissionKeys) => {
    return permissionKeys.some(key => permissions[key] === true);
  }, [permissions]);

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback((permissionKeys) => {
    return permissionKeys.every(key => permissions[key] === true);
  }, [permissions]);

  /**
   * Check if user is admin (has team.manage_settings)
   * Backwards compatible with old role checks
   */
  const isAdmin = useMemo(() => {
    return role === 'owner' || role === 'admin' || hasPermission('team.manage_settings');
  }, [role, hasPermission]);

  /**
   * Check if user is owner
   */
  const isOwner = useMemo(() => {
    return role === 'owner';
  }, [role]);

  /**
   * Check if user can manage team (invite, remove members)
   */
  const canManageTeam = useMemo(() => {
    return hasPermission('team.invite_members') || hasPermission('team.remove_members');
  }, [hasPermission]);

  /**
   * Check if user can view all tasks
   */
  const canViewAllTasks = useMemo(() => {
    return hasPermission('tasks.view_all');
  }, [hasPermission]);

  /**
   * Check if user can create tasks
   */
  const canCreateTasks = useMemo(() => {
    return hasPermission('tasks.create');
  }, [hasPermission]);

  return {
    permissions,
    role,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isOwner,
    canManageTeam,
    canViewAllTasks,
    canCreateTasks,
    refetch: fetchPermissions
  };
}

/**
 * Component to conditionally render children based on permissions
 * 
 * Usage:
 *   <PermissionGate permission="tasks.create">
 *     <CreateTaskButton />
 *   </PermissionGate>
 *   
 *   <PermissionGate permissions={['tasks.edit_own', 'tasks.edit_any']} requireAll={false}>
 *     <EditTaskButton />
 *   </PermissionGate>
 */
export function PermissionGate({ 
  children, 
  permission, 
  permissions: permissionList, 
  requireAll = false,
  fallback = null 
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  if (loading) {
    return fallback;
  }

  // Single permission check
  if (permission) {
    return hasPermission(permission) ? children : fallback;
  }

  // Multiple permissions check
  if (permissionList && permissionList.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(permissionList)
      : hasAnyPermission(permissionList);
    return hasAccess ? children : fallback;
  }

  // No permission specified, render children
  return children;
}

/**
 * Higher-order component to wrap components with permission check
 * 
 * Usage:
 *   const ProtectedComponent = withPermission(MyComponent, 'tasks.create');
 */
export function withPermission(Component, permissionKey) {
  return function ProtectedComponent(props) {
    const { hasPermission, loading } = usePermissions();

    if (loading) {
      return null;
    }

    if (!hasPermission(permissionKey)) {
      return null;
    }

    return <Component {...props} />;
  };
}

export default usePermissions;
